import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import GlobalHeader from '../components/GlobalHeader';
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext';
import { useTenant } from '../contexts/TenantContext';
import { formatCurrency, formatPercent, formatNumber } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { InfoTooltip } from '../components/InfoTooltip';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  PieChart,
  BarChart3,
  AlertTriangle,
  TrendingUp,
  Crown,
  Download
} from 'lucide-react';
import { ResponsiveContainer, ScatterChart as ReScatterChart, Scatter, CartesianGrid, XAxis, YAxis, Tooltip as ReTooltip, ReferenceLine } from 'recharts';

const FragilityTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 shadow-sm text-xs">
      <div className="font-semibold text-gray-800 mb-1 truncate max-w-[180px]" title={point.name}>{point.name}</div>
      <div className="text-gray-600">Revenue Share: <span className="font-medium text-gray-900">{formatPercent(point.revenue_share)}</span></div>
      <div className="text-gray-600">OOS%: <span className="font-medium text-gray-900">{formatPercent(point.oos_pct)}</span></div>
    </div>
  );
};

interface ConcentrationRiskData {
  week: string;
  cards: {
    topN_share_revenue: number | null;
    topN_share_units: number | null;
    hhi_revenue: number | null;
    hhi_units: number | null;
    single_sku_max_revenue_share: number | null;
    single_sku_max_units_share: number | null;
  };
  topN_list: Array<{
    rank: number;
    sku: string;
    name: string;
    revenue: number;
    revenue_share: number;
    units: number;
    units_share: number;
  }>;
  scatter: Array<{
    sku: string;
    name: string;
    revenue_share: number;
    oos_pct: number;
  }>;
}

const useConcentrationRiskData = (fiscalWeek: number, topN: number, retailerId: string | null) => {
  return useQuery<ConcentrationRiskData>({
    queryKey: ['concentration-risk', fiscalWeek, topN, retailerId],
    queryFn: async () => {
      const params = new URLSearchParams({
        week: fiscalWeek.toString(),
        topN: topN.toString()
      });
      
      // Add retailer_id if selected
      if (retailerId) {
        params.append('retailer_id', retailerId);
      }
      
      const response = await fetch(`/api/concentration-risk?${params}`);
      if (!response.ok) throw new Error('Failed to fetch concentration risk data');
      return response.json();
    },
    enabled: !!fiscalWeek
  });
};

export function ConcentrationRisk() {
  const { selectedFiscalWeek } = useFiscalWeekContext();
  const { selectedRetailerId } = useTenant();
  const [topN, setTopN] = useState(3);
  const [perspective, setPerspective] = useState<'revenue' | 'units'>('revenue');

  const { data, isLoading, error } = useConcentrationRiskData(
    selectedFiscalWeek, 
    topN,
    selectedRetailerId
  );

  const getHHIRiskLevel = (hhi: number | null) => {
    if (!hhi) return { level: 'Unknown', color: 'text-muted-foreground' };
    if (hhi < 0.15) return { level: 'Low Risk', color: 'text-green-600' };
    if (hhi < 0.25) return { level: 'Moderate Risk', color: 'text-yellow-600' };
    return { level: 'High Risk', color: 'text-red-600' };
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader
          title="Concentration & Risk"
          subtitle="Error loading concentration & risk data"
        />
        <div className="container mx-auto px-6 py-8">
          <div className="text-center text-red-600">
            Error loading concentration risk data: {(error as Error).message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader
        title="Concentration & Risk"
        subtitle="Revenue concentration analysis and SKU-level risk assessment"
        isLoading={isLoading}
      />
      
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">

        {isLoading ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-3"></div>
                  <div className="h-8 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Risk Assessment Summary (moved to top) */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-200 px-6 py-3">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                  <h3 className="text-base font-bold text-gray-900">Risk Assessment Summary</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="text-3xl font-bold text-blue-600 tracking-tight">
                      {data?.cards.topN_share_revenue ? formatPercent(data.cards.topN_share_revenue) : '—'}
                    </div>
                    <div className="text-sm font-semibold text-gray-900 mt-2">
                      Top-{topN} Revenue Concentration
                    </div>
                    <div className="text-xs text-gray-600 mt-2 font-medium">
                      {data?.cards.topN_share_revenue && data.cards.topN_share_revenue > 0.6 ? 'High concentration risk' : 
                       data?.cards.topN_share_revenue && data.cards.topN_share_revenue > 0.4 ? 'Moderate concentration' : 'Well diversified'}
                    </div>
                  </div>
                  <div className="text-center p-6 bg-green-50 rounded-xl border border-green-200">
                    <div className="text-3xl font-bold text-green-600 tracking-tight">
                      {data?.cards.hhi_revenue ? formatNumber(data.cards.hhi_revenue, 3) : '—'}
                    </div>
                    <div className="text-sm font-semibold text-gray-900 mt-2">
                      HHI Market Concentration
                    </div>
                    <div className={`text-xs text-gray-600 mt-2 font-medium ${getHHIRiskLevel(data?.cards.hhi_revenue).color.replace('text-muted-foreground', 'text-gray-500')}`}>
                      {getHHIRiskLevel(data?.cards.hhi_revenue).level}
                    </div>
                  </div>
                  <div className="text-center p-6 bg-red-50 rounded-xl border border-red-200">
                    <div className="text-3xl font-bold text-red-600 tracking-tight">
                      {data?.scatter?.filter(sku => sku.revenue_share > 0.05 && sku.oos_pct > 0.10).length || 0}
                    </div>
                    <div className="text-sm font-semibold text-gray-900 mt-2">
                      Fragile Whale SKUs
                    </div>
                    <div className="text-xs text-gray-600 mt-2 font-medium">
                      High share + high OOS risk
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200 px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Top N:</label>
                      <Select value={topN.toString()} onValueChange={(value) => setTopN(parseInt(value))}>
                        <SelectTrigger className="w-20 border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3</SelectItem>
                          <SelectItem value="5">5</SelectItem>
                          <SelectItem value="10">10</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Perspective:</label>
                      <Select value={perspective} onValueChange={(value: 'revenue' | 'units') => setPerspective(value)}>
                        <SelectTrigger className="w-32 border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="revenue">Revenue</SelectItem>
                          <SelectItem value="units">Units</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button variant="outline" size="sm" className="border-gray-300 hover:bg-gray-50">
                    <Download className="w-4 h-4 mr-2" />
                    Export Analysis
                  </Button>
                </div>
              </div>
            </div>

            {/* Header Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <PieChart className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Top-{topN} Revenue Share</span>
                  </div>
                  <InfoTooltip content={`Share of total revenue from top ${topN} SKUs by revenue. Higher concentration = higher risk.`} />
                </div>
                <div className="text-3xl font-bold text-gray-900 tracking-tight">
                  {data?.cards.topN_share_revenue ? formatPercent(data.cards.topN_share_revenue) : '—'}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <BarChart3 className="h-4 w-4 text-indigo-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Top-{topN} Units Share</span>
                  </div>
                  <InfoTooltip content={`Share of total units from top ${topN} SKUs by revenue. Volume concentration indicator.`} />
                </div>
                <div className="text-3xl font-bold text-gray-900 tracking-tight">
                  {data?.cards.topN_share_units ? formatPercent(data.cards.topN_share_units) : '—'}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <AlertTriangle className="h-4 w-4 text-orange-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">HHI (Revenue)</span>
                  </div>
                  <InfoTooltip content="Herfindahl-Hirschman Index for revenue. <0.15 = Low Risk, 0.15-0.25 = Moderate, >0.25 = High Risk" />
                </div>
                <div className="text-3xl font-bold text-gray-900 tracking-tight">
                  {data?.cards.hhi_revenue ? formatNumber(data.cards.hhi_revenue, 3) : '—'}
                </div>
                <div className={`text-xs mt-2 font-medium ${getHHIRiskLevel(data?.cards.hhi_revenue).color.replace('text-muted-foreground', 'text-gray-500')}`}>
                  {getHHIRiskLevel(data?.cards.hhi_revenue).level}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">HHI (Units)</span>
                  </div>
                  <InfoTooltip content="Herfindahl-Hirschman Index for units. Measures volume concentration across SKUs." />
                </div>
                <div className="text-3xl font-bold text-gray-900 tracking-tight">
                  {data?.cards.hhi_units ? formatNumber(data.cards.hhi_units, 3) : '—'}
                </div>
                <div className={`text-xs mt-2 font-medium ${getHHIRiskLevel(data?.cards.hhi_units).color.replace('text-muted-foreground', 'text-gray-500')}`}>
                  {getHHIRiskLevel(data?.cards.hhi_units).level}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Crown className="h-4 w-4 text-yellow-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Largest SKU Share</span>
                  </div>
                  <InfoTooltip content="Single SKU with highest revenue share. High single-SKU dependency indicator." />
                </div>
                <div className="text-3xl font-bold text-gray-900 tracking-tight">
                  {data?.cards.single_sku_max_revenue_share ? formatPercent(data.cards.single_sku_max_revenue_share) : '—'}
                </div>
                <div className="text-xs text-gray-600 mt-2 font-medium">
                  {perspective === 'revenue' ? 'Revenue' : 'Units'}
                </div>
              </div>
            </div>

            {/* Top-N Table and Scatter Plot */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Top-N Table */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-6 py-3">
                  <h3 className="text-base font-bold text-gray-900">Top-{topN} SKUs by Revenue</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Ranked by total revenue with share calculations
                  </p>
                </div>
                <div className="p-6">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-200">
                        <TableHead className="w-12 text-xs font-semibold text-gray-700 uppercase tracking-wide">Rank</TableHead>
                        <TableHead className="text-xs font-semibold text-gray-700 uppercase tracking-wide">SKU</TableHead>
                        <TableHead className="text-right text-xs font-semibold text-gray-700 uppercase tracking-wide">Revenue</TableHead>
                        <TableHead className="text-right text-xs font-semibold text-gray-700 uppercase tracking-wide">Share</TableHead>
                        <TableHead className="text-right text-xs font-semibold text-gray-700 uppercase tracking-wide">Units</TableHead>
                        <TableHead className="text-right text-xs font-semibold text-gray-700 uppercase tracking-wide">Units %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data?.topN_list.map((sku) => (
                        <TableRow key={sku.sku} className="border-gray-200 hover:bg-gray-50 transition-colors">
                          <TableCell>
                            <div className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-bold ${
                              sku.rank <= 3 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              #{sku.rank}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            <div>
                              <div className="font-semibold text-gray-900 text-sm">{sku.sku}</div>
                              <div className="text-xs text-gray-600 truncate max-w-[200px]">
                                {sku.name}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-sm text-gray-900 font-medium">
                            {formatCurrency(sku.revenue, 0)}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="inline-flex px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium border border-purple-200">
                              {formatPercent(sku.revenue_share)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right text-sm text-gray-900">
                            {formatNumber(sku.units, 0)}
                          </TableCell>
                          <TableCell className="text-right text-xs text-gray-600 font-medium">
                            {formatPercent(sku.units_share)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Fragility Scatter Plot */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-b border-gray-200 px-6 py-3">
                  <h3 className="text-base font-bold text-gray-900">Fragility Analysis</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Revenue share vs OOS% - identifies "fragile whales"
                  </p>
                </div>
                <div className="p-6">
                  <div className="h-80 bg-gray-50 rounded-lg border border-gray-200">
                    {data?.scatter && data.scatter.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <ReScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            type="number"
                            dataKey="revenue_share"
                            name="Revenue Share"
                            tickFormatter={(val) => formatPercent(val)}
                            domain={[0, 'dataMax']}
                          />
                          <YAxis
                            type="number"
                            dataKey="oos_pct"
                            name="OOS%"
                            tickFormatter={(val) => formatPercent(val)}
                            domain={[0, 'dataMax']}
                          />
                          <ReTooltip
                            cursor={{ strokeDasharray: '3 3' }}
                            content={<FragilityTooltip />}
                          />
                          <ReferenceLine x={0.05} stroke="#FED7D7" strokeDasharray="3 3" />
                          <ReferenceLine y={0.1} stroke="#FED7D7" strokeDasharray="3 3" />
                          <Scatter data={data.scatter} fill="#F97316" />
                        </ReScatterChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-center text-gray-500">
                        <div>
                          <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <div className="text-sm font-medium">
                            No SKUs qualified for plotting this week
                          </div>
                          <div className="text-xs mt-2 max-w-xs text-gray-600">
                            High revenue share + high OOS% = fragile whales (high risk)
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Fragile SKUs List */}
                  <div className="mt-6">
                    <div className="text-sm font-semibold text-gray-900 mb-3">High-Risk SKUs (&gt;5% share + &gt;10% OOS)</div>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {data?.scatter
                        .filter(sku => sku.revenue_share > 0.05 && sku.oos_pct > 0.10)
                        .slice(0, 5)
                        .map((sku) => (
                          <div key={sku.sku} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                            <span className="truncate flex-1 text-sm font-medium text-gray-900">{sku.name}</span>
                            <div className="flex gap-2 ml-3">
                              <span className="inline-flex px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium border border-red-200">
                                {formatPercent(sku.revenue_share)} rev
                              </span>
                              <span className="inline-flex px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium border border-orange-200">
                                {formatPercent(sku.oos_pct)} OOS
                              </span>
                            </div>
                          </div>
                        ))}
                      {data?.scatter.filter(sku => sku.revenue_share > 0.05 && sku.oos_pct > 0.10).length === 0 && (
                        <div className="text-sm text-gray-500 italic text-center py-4">
                          No high-risk fragile SKUs identified
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
